import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  approvePurchase,
  deletePurchase,
  downloadPurchasePdf,
  getPurchases,
  getPurchaseSuppliers,
} from "../api/purchasesApi";
import PurchaseActionDialog from "../components/PurchaseActionDialog";
import PurchaseCardShell from "../components/PurchaseCardShell";
import PurchaseFilters from "../components/PurchaseFilters";
import PurchasesTable from "../components/PurchasesTable";
import { getPurchaseErrorMessage } from "../utils/purchaseError";
import {
  normalizePurchaseList,
  normalizePurchaseSuppliers,
} from "../utils/purchaseModel";

const PAGE_SIZE = 20;
const SUPPLIER_SEARCH_DELAY_MS = 300;
const MISSING_RECEIPTS_PATTERN =
  /cantidad ingresada antes de aprobar|ingresos? vac[ií]os/i;

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !Array.isArray(response.data) &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

export default function ComprasPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestId = useRef(0);
  const latestSuppliersRequestId = useRef(0);
  const operationRef = useRef(false);
  const initialListState = useRef(location.state?.purchaseListState || {}).current;
  const supplierToRestoreRef = useRef(
    initialListState.filters?.supplierId || ""
  );
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierSearchInput, setSupplierSearchInput] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [filters, setFilters] = useState({
    supplierId: initialListState.filters?.supplierId || "",
    status: initialListState.filters?.status || "",
    dateFrom: initialListState.filters?.dateFrom || "",
    dateTo: initialListState.filters?.dateTo || "",
  });
  const [page, setPage] = useState(Number(initialListState.page) || 1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [suppliersError, setSuppliersError] = useState("");
  const [operationError, setOperationError] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage || ""
  );
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState("");
  const [approvalRequiresReview, setApprovalRequiresReview] = useState(false);
  const [activeOperation, setActiveOperation] = useState(null);
  const [reloadVersion, setReloadVersion] = useState(0);

  const invalidDateRange =
    filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo;

  const fetchPurchases = useCallback(
    async (signal) => {
      const requestId = ++latestRequestId.current;
      if (invalidDateRange) {
        setPurchases([]);
        setTotalItems(0);
        setTotalPages(0);
        setLoading(false);
        setListError("La fecha desde no puede ser posterior a la fecha hasta.");
        return;
      }

      setLoading(true);
      setListError("");
      try {
        const response = await getPurchases({
          page,
          limit: PAGE_SIZE,
          supplierId: filters.supplierId || undefined,
          status: filters.status || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          signal,
        });
        if (signal?.aborted || requestId !== latestRequestId.current) return;

        const result = normalizePurchaseList(unwrapHttp(response));
        if (result.meta.totalPages > 0 && page > result.meta.totalPages) {
          setPage(result.meta.totalPages);
          return;
        }
        setPurchases(result.data);
        setTotalItems(result.meta.totalItems);
        setTotalPages(result.meta.totalPages);
      } catch (error) {
        if (signal?.aborted || requestId !== latestRequestId.current) return;
        setPurchases([]);
        setTotalItems(0);
        setTotalPages(0);
        setListError(
          getPurchaseErrorMessage(error, "No se pudieron cargar las compras.")
        );
      } finally {
        if (!signal?.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    }, [filters, invalidDateRange, page]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchPurchases(controller.signal);
    return () => controller.abort();
  }, [fetchPurchases, reloadVersion]);

  useEffect(() => {
    const timeout = setTimeout(
      () => setSupplierSearch(supplierSearchInput.trim()),
      SUPPLIER_SEARCH_DELAY_MS
    );
    return () => clearTimeout(timeout);
  }, [supplierSearchInput]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++latestSuppliersRequestId.current;
    async function fetchSuppliers() {
      setSuppliersLoading(true);
      setSuppliersError("");
      try {
        const response = await getPurchaseSuppliers({
          limit: 100,
          search: supplierSearch || undefined,
          signal: controller.signal,
        });
        if (
          controller.signal.aborted ||
          requestId !== latestSuppliersRequestId.current
        ) {
          return;
        }
        const normalized = normalizePurchaseSuppliers(unwrapHttp(response));
        setSuppliers(normalized);
        if (supplierToRestoreRef.current) {
          const restoredSupplier = normalized.find(
            (supplier) =>
              Number(supplier.id) === Number(supplierToRestoreRef.current)
          );
          if (restoredSupplier) {
            setSelectedSupplier(restoredSupplier);
            supplierToRestoreRef.current = "";
          }
        }
      } catch (error) {
        if (
          controller.signal.aborted ||
          requestId !== latestSuppliersRequestId.current
        ) {
          return;
        }
        setSuppliers([]);
        setSuppliersError(
          getPurchaseErrorMessage(
            error,
            "No se pudieron cargar los proveedores para filtrar."
          )
        );
      } finally {
        if (
          !controller.signal.aborted &&
          requestId === latestSuppliersRequestId.current
        ) {
          setSuppliersLoading(false);
        }
      }
    }
    fetchSuppliers();
    return () => controller.abort();
  }, [supplierSearch]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin compras para mostrar";
    return `Mostrando ${purchases.length} de ${totalItems} compras`;
  }, [purchases.length, totalItems]);

  const hasFilters = Object.values(filters).some(Boolean);
  const visibleError = operationError || listError || suppliersError;

  function handleFilterChange(name, value) {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
    setOperationError("");
  }

  function handleSupplierChange(supplier) {
    setSelectedSupplier(supplier);
    handleFilterChange("supplierId", supplier ? String(supplier.id) : "");
  }

  function handleSupplierSearchChange(value, reason) {
    setSupplierSearchInput(value);
    if (
      reason === "input" &&
      selectedSupplier &&
      value !== selectedSupplier.name
    ) {
      setSelectedSupplier(null);
      handleFilterChange("supplierId", "");
    }
  }

  function handleOpen(purchase) {
    navigate(`/stock/operacion/compras/${purchase.id}`, {
      state: { purchaseListState: { filters, page } },
    });
  }

  async function handlePdf(purchase) {
    if (operationRef.current) return;
    operationRef.current = true;
    setOperationError("");
    setActiveOperation({ type: "pdf", purchaseId: purchase.id });
    try {
      await downloadPurchasePdf(purchase.id);
    } catch (error) {
      setOperationError(
        getPurchaseErrorMessage(error, "No se pudo generar el PDF de la compra.")
      );
    } finally {
      operationRef.current = false;
      setActiveOperation(null);
    }
  }

  function requestAction(type, purchase) {
    if (operationRef.current) return;
    setActionError("");
    setApprovalRequiresReview(false);
    setOperationError("");
    setPendingAction({ type, purchase });
  }

  function closeActionDialog() {
    if (operationRef.current) return;
    setActionError("");
    setApprovalRequiresReview(false);
    setPendingAction(null);
  }

  async function confirmAction() {
    const action = pendingAction;
    if (!action?.purchase?.id || operationRef.current) return;

    operationRef.current = true;
    setActionError("");
    setActiveOperation({ type: action.type, purchaseId: action.purchase.id });
    try {
      if (action.type === "approve") {
        await approvePurchase(action.purchase.id);
      } else {
        await deletePurchase(action.purchase.id);
      }

      const nextTotal = Math.max(
        0,
        totalItems - (action.type === "delete" ? 1 : 0)
      );
      const lastPage = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));
      setPendingAction(null);
      if (page > lastPage) setPage(lastPage);
      else setReloadVersion((current) => current + 1);
    } catch (error) {
      const fallback =
        action.type === "approve"
          ? "No se pudo aprobar la compra. Revisá que todos los ingresos estén completos."
          : "No se pudo eliminar la compra.";
      const message = getPurchaseErrorMessage(error, fallback);
      setActionError(message);
      setApprovalRequiresReview(
        action.type === "approve" && MISSING_RECEIPTS_PATTERN.test(message)
      );
    } finally {
      operationRef.current = false;
      setActiveOperation(null);
    }
  }

  function viewPendingPurchase() {
    const purchase = pendingAction?.purchase;
    if (!purchase?.id || operationRef.current) return;
    setActionError("");
    setApprovalRequiresReview(false);
    setPendingAction(null);
    handleOpen(purchase);
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader title="Compras" onBack={() => navigate("/stock/operacion")} />

      <PurchaseCardShell>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.25}
            sx={{
              alignItems: { xs: "stretch", lg: "center" },
              justifyContent: "space-between",
            }}
          >
            <PurchaseFilters
              {...filters}
              suppliers={suppliers}
              suppliersLoading={suppliersLoading}
              selectedSupplier={selectedSupplier}
              disabled={Boolean(activeOperation)}
              onSupplierChange={handleSupplierChange}
              onSupplierSearchChange={handleSupplierSearchChange}
              onChange={handleFilterChange}
            />

            <Button
              variant="contained"
              onClick={() => navigate("/stock/operacion/compras/crear")}
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-end", lg: "center" },
                minWidth: 170,
                height: 38,
                px: 3,
                borderRadius: "33px",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Agregar pedido
            </Button>
          </Stack>

          {successMessage ? (
            <Alert
              severity="success"
              onClose={() => setSuccessMessage("")}
              sx={{ borderRadius: "16px", fontWeight: 650 }}
            >
              {successMessage}
            </Alert>
          ) : null}

          {visibleError ? (
            <Alert
              severity="error"
              role="alert"
              action={
                listError && !invalidDateRange ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setReloadVersion((current) => current + 1)}
                  >
                    Reintentar
                  </Button>
                ) : undefined
              }
              onClose={operationError ? () => setOperationError("") : undefined}
              sx={{ borderRadius: "16px", fontWeight: 650 }}
            >
              {visibleError}
            </Alert>
          ) : null}

          <PurchasesTable
            rows={purchases}
            loading={loading}
            activeOperation={activeOperation}
            onOpen={handleOpen}
            onPdf={handlePdf}
            onApprove={(purchase) => requestAction("approve", purchase)}
            onDelete={(purchase) => requestAction("delete", purchase)}
            emptyMessage={
              hasFilters
                ? "No se encontraron compras con los filtros seleccionados."
                : "Todavía no hay compras cargadas."
            }
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              pt: 0.5,
            }}
          >
            <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}>
              {pageSummary}
            </Typography>
            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              color="primary"
              disabled={loading || Boolean(activeOperation) || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </PurchaseCardShell>

      <PurchaseActionDialog
        open={Boolean(pendingAction)}
        action={pendingAction?.type}
        purchase={pendingAction?.purchase}
        loading={
          Boolean(activeOperation) &&
          activeOperation?.type === pendingAction?.type &&
          activeOperation?.purchaseId === pendingAction?.purchase?.id
        }
        error={actionError}
        approvalRequiresReview={approvalRequiresReview}
        onClose={closeActionDialog}
        onConfirm={confirmAction}
        onViewPurchase={viewPendingPurchase}
      />
    </Stack>
  );
}
